/**
 * Hero right column — abstract product coming to life (not a technical explainer).
 */
import React from 'react';
import {heroProductPhases, heroMobileStory, heroConnectionNodes} from "../data";
import {HERO_PHASES} from "./useHeroAnimation";

function phaseLabel(phase) {
    const match = heroProductPhases.find((p) => p.id === phase);
    return match?.label || '';
}

const MOBILE_INDEX = {
    idea: 0,
    build: 0,
    intelligence: 1,
    connected: 1,
    production: 2
};

/** Abstract SaaS UI — morphs via parent data-phase (CSS). */
function AbstractProduct() {
    return (
        <div className="hx-app-shell" aria-hidden="true">
            <div className="hx-app-chrome">
                <span className="hx-app-dot"/>
                <span className="hx-app-dot"/>
                <span className="hx-app-dot"/>
                <span className="hx-app-title-bar"/>
            </div>
            <div className="hx-app-layout">
                <aside className="hx-app-nav">
                    <span/><span/><span/><span className="is-accent"/>
                </aside>
                <div className="hx-app-main">
                    <div className="hx-app-metrics">
                        <div className="hx-app-metric"/><div className="hx-app-metric"/><div className="hx-app-metric is-highlight"/>
                    </div>
                    <div className="hx-app-panel">
                        <div className="hx-app-row"/><div className="hx-app-row"/><div className="hx-app-row is-done"/>
                    </div>
                    <div className="hx-app-flow">
                        <span className="hx-app-node"/><span className="hx-app-flow-line"/>
                        <span className="hx-app-node is-active"/><span className="hx-app-flow-line"/>
                        <span className="hx-app-node"/>
                    </div>
                </div>
            </div>
            <div className="hx-app-glow"/>
        </div>
    );
}

function StaticLiveProduct() {
    return (
        <div className="hx-product-viz hx-product-static" data-phase="production">
            <div className="hx-product-grid-bg" aria-hidden="true"/>
            <AbstractProduct/>
            <div className="hx-live-pill"><span className="hx-live-dot"/> LIVE</div>
            <p className="hx-scale-line">Built to scale.</p>
            <p className="hx-built-by">Built by WorkAxis</p>
        </div>
    );
}

export default function HeroVisualization({phase, reducedMotion, hoverSlow, onHoverChange}) {
    const label = phaseLabel(phase);
    const mobileStep = MOBILE_INDEX[phase] ?? 0;

    if (reducedMotion) {
        return (
            <div
                className="hx-viz hx-product-viz-wrap hx-enter-viz"
                data-reduced="true"
                aria-label="Abstract software product in production, built by WorkAxis"
            >
                <StaticLiveProduct/>
            </div>
        );
    }

    return (
        <div
            className="hx-viz hx-product-viz-wrap hx-product-viz hx-enter-viz"
            data-phase={phase}
            data-hover={hoverSlow ? 'true' : 'false'}
            onMouseEnter={() => onHoverChange(true)}
            onMouseLeave={() => onHoverChange(false)}
            aria-label="Animation of an idea becoming a live software product"
        >
            <div className="hx-product-grid-bg" aria-hidden="true"/>

            <ul className="hx-story-mobile" aria-hidden="true">
                {heroMobileStory.map((step, i) => (
                    <li key={step} className={i === mobileStep ? 'is-active' : ''}>{step}</li>
                ))}
            </ul>

            <div className="hx-product-stage">
                {/* Idea seed */}
                <div className="hx-idea-card">
                    <span className="hx-idea-label">An idea</span>
                </div>

                {/* Floating fragments before the product forms */}
                <div className="hx-fragments">
                    <span className="hx-fragment hx-f1"/>
                    <span className="hx-fragment hx-f2"/>
                    <span className="hx-fragment hx-f3"/>
                    <span className="hx-fragment hx-f4"/>
                </div>

                <AbstractProduct/>

                {/* Ecosystem connections */}
                <div className="hx-orbit">
                    {heroConnectionNodes.map((node, i) => (
                        <span key={node} className="hx-orbit-node" style={{'--i': i}}>{node}</span>
                    ))}
                </div>

                {/* Intelligence pulse — no technical copy */}
                <div className="hx-smart-pulse" aria-hidden="true"/>
            </div>

            {label && (
                <div className="hx-phase-tag" key={phase}>{label}</div>
            )}

            <div className="hx-live-pill"><span className="hx-live-dot"/> LIVE</div>
            <p className="hx-scale-line">Built to scale.</p>
            <p className="hx-built-by">Built by WorkAxis</p>

            <p className="hx-viz-sr">
                Product story: {HERO_PHASES.join(', ')}. Current: {phase}.
            </p>
        </div>
    );
}
